/**
 * BlockList — 通用块列表渲染器。
 *
 * 所有递归场景（顶层、for 迭代、content 块体、fragment）都通过这一个组件渲染，
 * 确保行为一致。使用 groupBlocks() 将相邻 inline 块分组到 <p> 内。
 */

import type { DocumentBlock } from "@magic-md/core";
import { memo, useMemo } from "react";
import { areDocumentBlocksRenderEqual } from "../../../lib/document-block-equality";
import { cn } from "../../../lib/utils";
import { groupBlocks } from "../utils";
import { BlockDispatcher } from "./BlockDispatcher";
import { InlineGroup } from "./InlineGroup";

export interface BlockListProps {
    blocks: DocumentBlock[];
    className?: string;
    /** Optional wrapper around each top-level grouped item (e.g. LazyBlock for viewport culling). */
    blockWrapper?: (props: {
        children: React.ReactNode;
        block: DocumentBlock | DocumentBlock[];
        index: number;
        blockKey: string;
    }) => React.ReactNode;
}

export const BlockList = memo(function BlockList({ blocks, className, blockWrapper }: BlockListProps) {
    const grouped = useMemo(() => groupBlocks(blocks), [blocks]);
    const blockIndexes = useMemo(() => buildBlockIndexMap(blocks), [blocks]);

    if (grouped.length === 0) return null;

    const occurrences = new Map<string, number>();

    return (
        <div className={cn("magic-block-list", className)}>
            {grouped.map((item, i) => {
                const key = groupedBlockKey(item, occurrences);
                return (
                    <BlockListItem
                        key={key}
                        item={item}
                        origBlocks={blocks}
                        blockIndexes={blockIndexes}
                        blockIndices={getGroupedBlockIndices(item, blockIndexes, i)}
                        index={i}
                        blockKey={key}
                        blockWrapper={blockWrapper}
                    />
                );
            })}
        </div>
    );
});

interface BlockListItemProps {
    item: DocumentBlock | DocumentBlock[];
    origBlocks: DocumentBlock[];
    blockIndexes: ReadonlyMap<DocumentBlock, number>;
    blockIndices: number[];
    index: number;
    blockKey: string;
    blockWrapper?: BlockListProps["blockWrapper"];
}

const BlockListItem = memo(function BlockListItem({
    item,
    origBlocks,
    blockIndexes,
    blockIndices,
    index,
    blockKey,
    blockWrapper,
}: BlockListItemProps) {
    if (Array.isArray(item)) {
        const el = (
            <InlineGroup
                blocks={item}
                origBlocks={origBlocks}
                blockIndexes={blockIndexes}
            />
        );
        return blockWrapper
            ? blockWrapper({ children: el, block: item, index, blockKey })
            : el;
    }

    const el = (
        <BlockDispatcher
            block={item}
            blockIndex={blockIndices[0] ?? index}
        />
    );
    return blockWrapper
        ? blockWrapper({ children: el, block: item, index, blockKey })
        : el;
}, areBlockListItemPropsEqual);

function buildBlockIndexMap(blocks: DocumentBlock[]): Map<DocumentBlock, number> {
    const indexes = new Map<DocumentBlock, number>();
    blocks.forEach((block, index) => {
        if (!indexes.has(block)) indexes.set(block, index);
    });
    return indexes;
}

function getGroupedBlockIndices(
    item: DocumentBlock | DocumentBlock[],
    blockIndexes: ReadonlyMap<DocumentBlock, number>,
    fallbackIndex: number,
): number[] {
    if (Array.isArray(item)) {
        return item.map((block, index) => blockIndexes.get(block) ?? fallbackIndex + index);
    }
    return [blockIndexes.get(item) ?? fallbackIndex];
}

function areBlockListItemPropsEqual(
    prev: BlockListItemProps,
    next: BlockListItemProps,
): boolean {
    return (
        prev.blockKey === next.blockKey &&
        prev.blockWrapper === next.blockWrapper &&
        (!needsBlockIndexForRender(prev.item) ||
            areNumberArraysEqual(prev.blockIndices, next.blockIndices)) &&
        areGroupedBlocksEqual(prev.item, next.item)
    );
}

function areGroupedBlocksEqual(
    left: DocumentBlock | DocumentBlock[],
    right: DocumentBlock | DocumentBlock[],
): boolean {
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        if (left.length !== right.length) return false;
        return left.every((block, index) =>
            areDocumentBlocksRenderEqual(block, right[index]),
        );
    }
    return areDocumentBlocksRenderEqual(left, right);
}

function areNumberArraysEqual(left: readonly number[], right: readonly number[]): boolean {
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
}

function needsBlockIndexForRender(item: DocumentBlock | DocumentBlock[]): boolean {
    const blocks = Array.isArray(item) ? item : [item];
    return blocks.some((block) =>
        block.type === "markdown" && /(^|\n) {0,3}#{1,6}[ \t]+/.test(block.content),
    );
}

export function groupedBlockKey(
    block: DocumentBlock | DocumentBlock[],
    occurrences: Map<string, number> = new Map(),
): string {
    const identity = groupedBlockIdentity(block);
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return `${identity}-${occurrence}`;
}

function groupedBlockIdentity(block: DocumentBlock | DocumentBlock[]): string {
    if (Array.isArray(block)) {
        const inlineIds = block
            .filter(
                (b): b is Extract<DocumentBlock, { type: "inline-render" }> | Extract<DocumentBlock, { type: "inline-ref" }> =>
                    b.type === "inline-render" || b.type === "inline-ref",
            )
            .map((b) => "definitionId" in b ? b.definitionId : "")
            .join("-");
        return `group-${inlineIds || "g"}`;
    }

    switch (block.type) {
        case "markdown":
            return `markdown-${stableStringHash(block.content)}`;
        case "block-render":
            return `render-${block.definitionId}`;
        case "inline-render":
            return `inline-${block.definitionId}`;
        case "inline-ref":
            return `ref-${block.definitionId}-${block.accessor}-${block.outputPath}`;
        case "for-block":
            return `for-${block.id}`;
        case "definition":
            return `def-${block.id}`;
        default:
            return "unknown";
    }
}

function stableStringHash(value: string): string {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}
